import React, { useState, useEffect } from 'react';
import {
  Box,
  Container,
  Typography,
  Paper,
  Button,
  TextField,
  FormControlLabel,
  Switch,
  MenuItem,
  Select,
  FormControl,
  InputLabel,
  Grid,
  Alert,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Tabs,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
} from '@mui/material';
import { useNavigate, useParams } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { api } from '../../../../services/authService';
import { selectCurrentToken } from '../../../../redux/authSlice';
import { ArrowBack as ArrowBackIcon } from '@mui/icons-material';
import moment from 'moment-timezone';
import LeadSelectionInterface from '../../../../components/LeadSelectionInterface';

// Get list of timezones from moment-timezone
const timezones = moment.tz.names();

const EditJourneyBasedCampaign = () => {
  const navigate = useNavigate();
  const { campaignId } = useParams();
  const dispatch = useDispatch();
  const token = useSelector(selectCurrentToken);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [journeys, setJourneys] = useState([]);
  const [accounts, setAccounts] = useState([]);
  const [crmCampaigns, setCrmCampaigns] = useState([]);
  const [statusChoices, setStatusChoices] = useState([]);
  const [activateDialogOpen, setActivateDialogOpen] = useState(false);
  const [activeTab, setActiveTab] = useState(0);
  const [saveSuccess, setSaveSuccess] = useState(false);
  const [participants, setParticipants] = useState([]);
  const [loadingParticipants, setLoadingParticipants] = useState(false);
  const [participantsError, setParticipantsError] = useState(null);

  // Form state
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    account: '',
    crm_campaign: '',
    start_date: '',
    end_date: '',
    active: true,
    is_ongoing: false,
    journey: '',
    campaign_type: 'journey',
    status: 'draft',
    auto_enroll_new_leads: false,
    auto_enroll_filters: [],
    config: {
      track_opens: true,
      track_clicks: true,
      track_replies: true,
      track_delivery: true,
      allow_opt_out: true,
      opt_out_message: '',
      from_email: '',
      from_name: '',
      reply_to: '',
      priority: 'normal',
      attachments: [],
      from_number: '',
      media_urls: [],
      voice: 'female',
      language: 'en-US',
      retry_attempts: 3,
      retry_delay: 300,
      record_call: true,
      call_timeout: 60,
      machine_detection: 'prefer_human',
      platform: 'whatsapp',
      quick_replies: []
    },
    journey_schedule: {
      business_hours_only: true,
      start_time: '09:00',
      end_time: '17:00',
      exclude_weekends: true,
      exclude_holidays: true,
      min_step_delay: 60,
      max_steps_per_day: 3,
      max_retry_attempts: 3,
      retry_delay_minutes: 60,
      step_timeout_minutes: 1440,
      allow_parallel_steps: false,
      max_parallel_steps: 1,
      timezone: 'UTC',
      is_active: true
    },
    lead_filters: []
  });

  // Test state
  const [testData, setTestData] = useState({
    email: '',
    phone: '',
    testStatus: null,
    testError: null,
    selectedStep: '',
  });

  // Load campaign data on component mount
  useEffect(() => {
    const fetchCampaignData = async () => {
      try {
        const [campaignResponse, accountsResponse, statusResponse] = await Promise.all([
          api.get(`/acs/nurturing-campaigns/${campaignId}/`),
          api.get('/crm/accounts/'),
          api.get('/acs/nurturing-campaigns/status_choices/')
        ]);

        const campaign = campaignResponse.data;
        setFormData({
          ...campaign,
          config: {
            ...formData.config,
            ...campaign.config
          },
          journey_schedule: campaign.journey_schedule || formData.journey_schedule,
        });

        setAccounts(accountsResponse.data);
        setStatusChoices(statusResponse.data);

        // Load CRM campaigns for the selected account
        if (campaign.account) {
          const crmCampaignsResponse = await api.get(`/crm/accounts/${campaign.account}/campaigns/`);
          setCrmCampaigns(crmCampaignsResponse.data);
        }

        // Load journeys for the selected account and CRM campaign
        if (campaign.account && campaign.crm_campaign) {
          const journeysResponse = await api.get(`/acs/journeys/?account_id=${campaign.account}&crm_campaign_id=${campaign.crm_campaign}`);
          setJourneys(journeysResponse.data);
        }
      } catch (err) {
        setError('Failed to load campaign data');
        console.error('Error loading campaign data:', err);
      }
    };
    fetchCampaignData();
  }, [campaignId]);

  // Fetch CRM campaigns when account changes
  useEffect(() => {
    const fetchCrmCampaigns = async () => {
      if (formData.account) {
        try {
          const response = await api.get(`/crm/accounts/${formData.account}/campaigns/`);
          setCrmCampaigns(response.data);
        } catch (err) {
          setError('Failed to load CRM campaigns');
          console.error('Error loading CRM campaigns:', err);
        }
      } else {
        setCrmCampaigns([]);
      }
    };
    fetchCrmCampaigns();
  }, [formData.account]);

  // Load journeys when account and CRM campaign are selected
  useEffect(() => {
    const fetchJourneys = async () => {
      if (!formData.account || !formData.crm_campaign) {
        setJourneys([]);
        return;
      }

      try {
        const response = await api.get(`/acs/journeys/?account_id=${formData.account}&crm_campaign_id=${formData.crm_campaign}`);
        setJourneys(response.data);
        
        // If current selected journey doesn't belong to new account/campaign, clear it
        if (formData.journey) {
          const journeyExists = response.data.some(j => j.id === formData.journey);
          if (!journeyExists) {
            setFormData(prev => ({
              ...prev,
              journey: ''
            }));
          }
        }
      } catch (err) {
        setError('Failed to load journeys for selected account and campaign');
        console.error('Error loading journeys:', err);
      }
    };
    fetchJourneys();
  }, [formData.account, formData.crm_campaign]);

  // Add useEffect to fetch participants when campaign data is loaded
  useEffect(() => {
    if (formData.account && campaignId) {
      fetchParticipants();
    }
  }, [formData.account, campaignId]);

  const fetchParticipants = async () => {
    setLoadingParticipants(true);
    setParticipantsError(null);
    try {
      const response = await api.get('/acs/lead-nurturing-participants/', {
        params: {
          campaign: campaignId,
          account: formData.account
        }
      });
      setParticipants(response.data);
    } catch (err) {
      setParticipantsError('Failed to load campaign participants');
      console.error('Error loading participants:', err);
    } finally {
      setLoadingParticipants(false);
    }
  };

  const getStatusColor = (status) => {
    const colors = {
      active: 'success',
      completed: 'info',
      paused: 'warning',
      failed: 'error',
      pending: 'default'
    };
    return colors[status] || 'default';
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleString();
  };

  const handleInputChange = (e) => {
    const { name, value, checked } = e.target;
    
    if (name === 'account') {
      setFormData(prev => ({
        ...prev,
        [name]: value,
        journey: '', // Clear journey when account changes
        crm_campaign: '' // Clear campaign when account changes
      }));
    } else if (name === 'journey') {
      const selectedJourney = journeys.find(j => j.id === value);
      setFormData((prev) => ({
        ...prev,
        [name]: value,
        crm_campaign: selectedJourney?.campaign || '',
      }));
    } else if (name.startsWith('journey_schedule.')) {
      const scheduleKey = name.split('.')[1];
      setFormData((prev) => ({
        ...prev,
        journey_schedule: {
          ...prev.journey_schedule,
          [scheduleKey]: e.target.type === 'checkbox' ? checked : value,
        },
      }));
    } else if (name.startsWith('config.')) {
      const configKey = name.split('.')[1];
      setFormData((prev) => ({
        ...prev,
        config: {
          ...prev.config,
          [configKey]: e.target.type === 'checkbox' ? checked : value,
        },
      }));
    } else {
      setFormData((prev) => ({
        ...prev,
        [name]: e.target.type === 'checkbox' ? checked : value,
      }));
    }
  };

  const handleSubmit = async () => {
    setLoading(true);
    setError(null);
    setSaveSuccess(false);

    try {
      // Update the campaign
      await api.put(`/acs/nurturing-campaigns/${campaignId}/`, {
        ...formData,
        updated_by: token.user_id
      });

      // Update lead assignments if filters are present
      if (formData.lead_filters.length > 0) {
        await api.post(`/acs/nurturing-campaigns/${campaignId}/assign_leads/`, {
          filters: formData.lead_filters,
          status: 'active'
        });
      }

      setSaveSuccess(true);
      setTimeout(() => setSaveSuccess(false), 3000);
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to update campaign');
    } finally {
      setLoading(false);
    }
  };

  const handleActivate = async () => {
    setLoading(true);
    setError(null);

    try {
      await api.post(`/acs/nurturing-campaigns/${campaignId}/activate/`);
      setActivateDialogOpen(false);
      navigate('/crm/lead-nurturing');
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to activate campaign');
    } finally {
      setLoading(false);
    }
  };

  const handleTestJourney = async () => {
    setTestData(prev => ({ ...prev, testStatus: 'loading', testError: null }));
    try {
      const testPayload = {
        journey_id: formData.journey,
        step_id: testData.selectedStep,
        recipient: testData.email || testData.phone
      };
      
      await api.post('/acs/test-journey/', testPayload);
      setTestData(prev => ({ ...prev, testStatus: 'success', testError: null }));
    } catch (err) {
      setTestData(prev => ({
        ...prev,
        testStatus: 'error',
        testError: err.response?.data?.message || 'Failed to send test message'
      }));
    }
  };

  const handleTabChange = (event, newValue) => {
    setActiveTab(newValue);
  };

  const renderBasicInfo = () => (
    <Grid container spacing={3}>
      <Grid item xs={12} md={6}>
        <FormControl fullWidth required>
          <InputLabel>Account</InputLabel>
          <Select
            name="account"
            value={formData.account}
            onChange={handleInputChange}
            label="Account"
          >
            {accounts.map((account) => (
              <MenuItem key={account.id} value={account.id}>
                {account.name}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      </Grid>
      <Grid item xs={12} md={6}>
        <FormControl fullWidth required>
          <InputLabel>CRM Campaign</InputLabel>
          <Select
            name="crm_campaign"
            value={formData.crm_campaign}
            onChange={handleInputChange}
            label="CRM Campaign"
            disabled={!formData.account}
          >
            {crmCampaigns.map((campaign) => (
              <MenuItem key={campaign.id} value={campaign.id}>
                {campaign.name}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      </Grid>
      <Grid item xs={12}>
        <TextField
          fullWidth
          required
          label="Campaign Name"
          name="name"
          value={formData.name}
          onChange={handleInputChange}
        />
      </Grid>
      <Grid item xs={12}>
        <TextField
          fullWidth
          multiline
          rows={3}
          label="Description"
          name="description"
          value={formData.description}
          onChange={handleInputChange}
        />
      </Grid>
      <Grid item xs={12} md={6}>
        <TextField
          fullWidth
          required
          label="Start Date"
          name="start_date"
          type="datetime-local"
          value={formData.start_date}
          onChange={handleInputChange}
          InputLabelProps={{
            shrink: true,
          }}
        />
      </Grid>
      <Grid item xs={12} md={6}>
        <TextField
          fullWidth
          label="End Date"
          name="end_date"
          type="datetime-local"
          value={formData.end_date}
          onChange={handleInputChange}
          disabled={formData.is_ongoing}
          InputLabelProps={{
            shrink: true,
          }}
        />
      </Grid>
      <Grid item xs={12} md={6}>
        <FormControlLabel
          control={
            <Switch
              checked={formData.is_ongoing}
              onChange={handleInputChange}
              name="is_ongoing"
            />
          }
          label="Ongoing Campaign"
        />
        <Typography variant="caption" color="textSecondary" display="block">
          If enabled, the campaign will run indefinitely
        </Typography>
      </Grid>
    </Grid>
  );

  const renderJourneyConfig = () => (
    <Grid container spacing={3}>
      <Grid item xs={12}>
        <FormControl fullWidth required>
          <InputLabel>Journey</InputLabel>
          <Select
            name="journey"
            value={formData.journey}
            onChange={handleInputChange}
            label="Journey"
            disabled={!formData.account || !formData.crm_campaign}
          >
            {journeys.map((journey) => (
              <MenuItem key={journey.id} value={journey.id}>
                {journey.name}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      </Grid>
      <Grid item xs={12}>
        <Typography variant="subtitle1" gutterBottom>
          Tracking Configuration
        </Typography>
        <Grid container spacing={2}>
          <Grid item xs={12} sm={6}>
            <FormControlLabel
              control={
                <Switch
                  checked={formData.config.track_opens}
                  onChange={handleInputChange}
                  name="config.track_opens"
                />
              }
              label="Track Opens"
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <FormControlLabel
              control={
                <Switch
                  checked={formData.config.track_clicks}
                  onChange={handleInputChange}
                  name="config.track_clicks"
                />
              }
              label="Track Clicks"
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <FormControlLabel
              control={
                <Switch
                  checked={formData.config.track_replies}
                  onChange={handleInputChange}
                  name="config.track_replies"
                />
              }
              label="Track Replies"
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <FormControlLabel
              control={
                <Switch
                  checked={formData.config.track_delivery}
                  onChange={handleInputChange}
                  name="config.track_delivery"
                />
              }
              label="Track Delivery"
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <FormControlLabel
              control={
                <Switch
                  checked={formData.config.allow_opt_out}
                  onChange={handleInputChange}
                  name="config.allow_opt_out"
                />
              }
              label="Allow Opt Out"
            />
          </Grid>
        </Grid>
      </Grid>
      {formData.config.allow_opt_out && (
        <Grid item xs={12}>
          <TextField
            fullWidth
            multiline
            rows={2}
            label="Opt Out Message"
            name="config.opt_out_message"
            value={formData.config.opt_out_message}
            onChange={handleInputChange}
          />
        </Grid>
      )}
    </Grid>
  );

  const renderScheduleSettings = () => (
    <Grid container spacing={3}>
      <Grid item xs={12}>
        <FormControl fullWidth required>
          <InputLabel>Timezone</InputLabel>
          <Select
            name="journey_schedule.timezone"
            value={formData.journey_schedule.timezone}
            onChange={handleInputChange}
            label="Timezone"
          >
            {timezones.map((tz) => (
              <MenuItem key={tz} value={tz}>
                {tz}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      </Grid>
      <Grid item xs={12} md={6}>
        <TextField
          fullWidth
          type="time"
          label="Start Time"
          name="journey_schedule.start_time"
          value={formData.journey_schedule.start_time}
          onChange={handleInputChange}
          InputLabelProps={{ shrink: true }}
        />
      </Grid>
      <Grid item xs={12} md={6}>
        <TextField
          fullWidth
          type="time"
          label="End Time"
          name="journey_schedule.end_time"
          value={formData.journey_schedule.end_time}
          onChange={handleInputChange}
          InputLabelProps={{ shrink: true }}
        />
      </Grid>
      <Grid item xs={12} md={6}>
        <TextField
          fullWidth
          type="number"
          label="Min Step Delay (minutes)"
          name="journey_schedule.min_step_delay"
          value={formData.journey_schedule.min_step_delay}
          onChange={handleInputChange}
          InputProps={{ inputProps: { min: 0 } }}
        />
      </Grid>
      <Grid item xs={12} md={6}>
        <TextField
          fullWidth
          type="number"
          label="Max Steps Per Day"
          name="journey_schedule.max_steps_per_day"
          value={formData.journey_schedule.max_steps_per_day}
          onChange={handleInputChange}
          InputProps={{ inputProps: { min: 1 } }}
        />
      </Grid>
      <Grid item xs={12} md={6}>
        <TextField
          fullWidth
          type="number"
          label="Max Retry Attempts"
          name="journey_schedule.max_retry_attempts"
          value={formData.journey_schedule.max_retry_attempts}
          onChange={handleInputChange}
          InputProps={{ inputProps: { min: 0 } }}
        />
      </Grid>
      <Grid item xs={12} md={6}>
        <TextField
          fullWidth
          type="number"
          label="Retry Delay (minutes)"
          name="journey_schedule.retry_delay_minutes"
          value={formData.journey_schedule.retry_delay_minutes}
          onChange={handleInputChange}
          InputProps={{ inputProps: { min: 1 } }}
        />
      </Grid>
      <Grid item xs={12} md={6}>
        <TextField
          fullWidth
          type="number"
          label="Step Timeout (minutes)"
          name="journey_schedule.step_timeout_minutes"
          value={formData.journey_schedule.step_timeout_minutes}
          onChange={handleInputChange}
          InputProps={{ inputProps: { min: 1 } }}
        />
      </Grid>
      <Grid item xs={12} md={6}>
        <FormControlLabel
          control={
            <Switch
              checked={formData.journey_schedule.allow_parallel_steps}
              onChange={handleInputChange}
              name="journey_schedule.allow_parallel_steps"
            />
          }
          label="Allow Parallel Steps"
        />
      </Grid>
      {formData.journey_schedule.allow_parallel_steps && (
        <Grid item xs={12} md={6}>
          <TextField
            fullWidth
            type="number"
            label="Max Parallel Steps"
            name="journey_schedule.max_parallel_steps"
            value={formData.journey_schedule.max_parallel_steps}
            onChange={handleInputChange}
            InputProps={{ inputProps: { min: 1 } }}
          />
        </Grid>
      )}
      <Grid item xs={12} md={6}>
        <FormControlLabel
          control={
            <Switch
              checked={formData.journey_schedule.business_hours_only}
              onChange={handleInputChange}
              name="journey_schedule.business_hours_only"
            />
          }
          label="Business Hours Only"
        />
      </Grid>
      <Grid item xs={12} md={6}>
        <FormControlLabel
          control={
            <Switch
              checked={formData.journey_schedule.exclude_weekends}
              onChange={handleInputChange}
              name="journey_schedule.exclude_weekends"
            />
          }
          label="Exclude Weekends"
        />
      </Grid>
      <Grid item xs={12} md={6}>
        <FormControlLabel
          control={
            <Switch
              checked={formData.journey_schedule.exclude_holidays}
              onChange={handleInputChange}
              name="journey_schedule.exclude_holidays"
            />
          }
          label="Exclude Holidays"
        />
      </Grid>
    </Grid>
  );

  const renderLeadSelection = () => (
    <Grid container spacing={3}>
      <Grid item xs={12}>
        <Paper sx={{ p: 2, mb: 2 }}>
          <Typography variant="subtitle1" gutterBottom>
            Auto-Enrollment Configuration
          </Typography>
          <FormControlLabel
            control={
              <Switch
                checked={formData.auto_enroll_new_leads}
                onChange={handleInputChange}
                name="auto_enroll_new_leads"
              />
            }
            label="Automatically enroll new leads that match the criteria"
          />
          {formData.auto_enroll_new_leads && (
            <Typography variant="body2" color="textSecondary" sx={{ mt: 1 }}>
              New leads will be automatically enrolled in this campaign if they match the filters defined below.
            </Typography>
          )}
        </Paper>
      </Grid>
      <Grid item xs={12}>
        <LeadSelectionInterface
          accountId={formData.account}
          campaignId={formData.crm_campaign}
          onFiltersChange={(filters) => {
            setFormData(prev => ({
              ...prev,
              lead_filters: filters,
              auto_enroll_filters: formData.auto_enroll_new_leads ? filters : []
            }));
          }}
        />
      </Grid>

      {/* Participants Section */}
      <Grid item xs={12}>
        <Paper sx={{ p: 2, mt: 3 }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
            <Typography variant="h6">Campaign Participants</Typography>
            <Button
              variant="outlined"
              onClick={fetchParticipants}
              disabled={loadingParticipants}
              startIcon={loadingParticipants ? <CircularProgress size={20} /> : null}
            >
              Refresh
            </Button>
          </Box>

          {participantsError && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {participantsError}
            </Alert>
          )}

          <TableContainer>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>Lead</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell>Entered At</TableCell>
                  <TableCell>Last Updated</TableCell>
                  <TableCell>Current Step</TableCell>
                  <TableCell>Messages Sent</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {loadingParticipants ? (
                  <TableRow>
                    <TableCell colSpan={6} align="center">
                      <CircularProgress />
                    </TableCell>
                  </TableRow>
                ) : participants.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={6} align="center">
                      No participants found
                    </TableCell>
                  </TableRow>
                ) : (
                  participants.map((participant) => (
                    <TableRow key={participant.id}>
                      <TableCell>
                        {participant?.lead_name || participant?.email || participant?.phone || 'N/A'}
                      </TableCell>
                      <TableCell>
                        <Chip
                          label={participant.status}
                          color={getStatusColor(participant.status)}
                          size="small"
                        />
                      </TableCell>
                      <TableCell>{formatDate(participant.entered_campaign_at)}</TableCell>
                      <TableCell>{formatDate(participant.updated_at)}</TableCell>
                      <TableCell>
                        {participant.current_journey_step || 'N/A'}
                      </TableCell>
                      <TableCell>
                        {participant.messages_sent_count || 0}
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </Paper>
      </Grid>
    </Grid>
  );

  const renderTestSection = () => (
    <Grid container spacing={3}>
      <Grid item xs={12}>
        <Typography variant="h6" gutterBottom>
          Test Your Journey
        </Typography>
        <Paper sx={{ p: 3 }}>
          <Grid container spacing={2}>
            <Grid item xs={12}>
              <Typography variant="subtitle1" gutterBottom>
                Select a step to test
              </Typography>
              <FormControl fullWidth>
                <InputLabel>Journey Step</InputLabel>
                <Select
                  value={testData.selectedStep}
                  onChange={(e) => setTestData(prev => ({ ...prev, selectedStep: e.target.value }))}
                  label="Journey Step"
                >
                  {journeys.find(j => j.id === formData.journey)?.steps?.map((step) => (
                    <MenuItem key={step.id} value={step.id}>
                      {step.name}
                    </MenuItem>
                  )) || []}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                type="email"
                label="Test Email"
                value={testData.email}
                onChange={(e) => setTestData(prev => ({ ...prev, email: e.target.value }))}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                type="tel"
                label="Test Phone"
                value={testData.phone}
                onChange={(e) => setTestData(prev => ({ ...prev, phone: e.target.value }))}
              />
            </Grid>
            <Grid item xs={12}>
              <Button
                variant="contained"
                onClick={handleTestJourney}
                disabled={testData.testStatus === 'loading' || !testData.selectedStep || (!testData.email && !testData.phone)}
              >
                {testData.testStatus === 'loading' ? (
                  <CircularProgress size={24} />
                ) : (
                  'Send Test'
                )}
              </Button>
            </Grid>
            {testData.testStatus === 'success' && (
              <Grid item xs={12}>
                <Alert severity="success">
                  Test message sent successfully!
                </Alert>
              </Grid>
            )}
            {testData.testStatus === 'error' && (
              <Grid item xs={12}>
                <Alert severity="error">
                  {testData.testError}
                </Alert>
              </Grid>
            )}
          </Grid>
        </Paper>
      </Grid>
    </Grid>
  );

  return (
    <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
      <Paper sx={{ p: 3 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 4 }}>
          <Button
            startIcon={<ArrowBackIcon />}
            onClick={() => navigate('/crm/lead-nurturing')}
            sx={{ mr: 2 }}
          >
            Back
          </Button>
          <Typography variant="h4" gutterBottom sx={{ flexGrow: 1 }}>
            Edit Journey-Based Campaign
          </Typography>
          <Box sx={{ display: 'flex', gap: 2 }}>
            {formData.status === 'draft' && (
              <Button
                variant="contained"
                color="success"
                onClick={() => setActivateDialogOpen(true)}
              >
                Activate Campaign
              </Button>
            )}
            <Button
              variant="contained"
              onClick={handleSubmit}
              disabled={loading}
            >
              {loading ? <CircularProgress size={24} /> : 'Save Changes'}
            </Button>
          </Box>
        </Box>

        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        {saveSuccess && (
          <Alert severity="success" sx={{ mb: 2 }}>
            Campaign updated successfully!
          </Alert>
        )}

        <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 3 }}>
          <Tabs value={activeTab} onChange={handleTabChange}>
            <Tab label="Basic Information" />
            <Tab label="Journey Configuration" />
            <Tab label="Schedule Settings" />
            <Tab label="Lead Selection" />
            <Tab label="Test" />
          </Tabs>
        </Box>

        <Box sx={{ mt: 3 }}>
          {activeTab === 0 && renderBasicInfo()}
          {activeTab === 1 && renderJourneyConfig()}
          {activeTab === 2 && renderScheduleSettings()}
          {activeTab === 3 && renderLeadSelection()}
          {activeTab === 4 && renderTestSection()}
        </Box>
      </Paper>

      <Dialog
        open={activateDialogOpen}
        onClose={() => setActivateDialogOpen(false)}
      >
        <DialogTitle>Activate Campaign</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Are you sure you want to activate this campaign? Once activated, the campaign will start sending messages according to the schedule.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setActivateDialogOpen(false)}>Cancel</Button>
          <Button onClick={handleActivate} color="success" variant="contained">
            Activate
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
};

export default EditJourneyBasedCampaign; 